import React, { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import Checkbox from "../common/checkbox";
import { Input } from "../common/input";
import apiInstance from "../../api/api";
import { useNavigate } from "react-router";
import routeNames from "../../constants/routeNames";
import { errorHandler } from "../../helpers/errorHelper";
import Spinner from "../common/spinner";

/**
 * @typedef {Object} FormData
 * @property {string} firstName - The user's first name.
 * @property {string} lastName - The user's last name.
 * @property {string} email - The user's email address.
 * @property {boolean} terms - Whether the user agreed to the terms.
 * @property {boolean} offers - Whether the user opted into marketing.
 */

/**
 * @returns {JSX.Element} The rendered form.
 */
export default function SignupForm() {
  const navigate = useNavigate();
  const { control, handleSubmit } = useForm();
  const [isLoading, setIsLoading] = useState(false);

  /**
   * @param {FormData} data
   */
  const onSubmit = async (data) => {
    try {
      setIsLoading(true);
      const response = await apiInstance.register(data);
      if (response?.result && response?.data) {
        navigate(routeNames.SUCCESS, {
          state: {
            redirected: true,
          },
        });
      } else {
        console.error(response?.error);
        errorHandler(response, navigate);
      }
    } catch (error) {
      console.error("Submission error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="mx-auto w-full space-y-4 text-start"
    >
      <Controller
        name="firstName"
        control={control}
        defaultValue=""
        rules={{
          required: "First Name is required",
          validate: (value) =>
            value.trim().length > 0 || "Please enter a valid first name",
        }}
        render={({ field, fieldState: { error } }) => (
          <Input
            {...field}
            type="text"
            placeholder="First Name"
            errorMessage={error?.message}
          />
        )}
      />
      <Controller
        name="lastName"
        control={control}
        defaultValue=""
        rules={{
          required: "Last Name is required",
          validate: (value) =>
            value.trim().length > 0 || "Please enter a valid last name",
        }}
        render={({ field, fieldState: { error } }) => (
          <Input
            {...field}
            type="text"
            placeholder="Last Name"
            errorMessage={error?.message}
          />
        )}
      />
      <Controller
        name="email"
        control={control}
        defaultValue=""
        rules={{
          required: "Email is required",
          pattern: {
            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
            message: "Please enter a valid email address",
          },
        }}
        render={({ field, fieldState: { error } }) => (
          <Input
            {...field}
            type="email"
            placeholder="Email Address"
            errorMessage={error?.message}
          />
        )}
      />
      <Controller
        name="terms"
        control={control}
        defaultValue={false}
        rules={{ required: "You must agree to our terms and conditions" }}
        render={({ field, fieldState: { error } }) => (
          <Checkbox
            label={`I have read and agree to the <a href="/terms" className="underline" target="_blank">terms and conditions</a>.`}
            checked={field.value}
            onChange={field.onChange}
            errorMessage={error?.message}
          />
        )}
      />
      <Controller
        name="offers"
        control={control}
        defaultValue={false}
        render={({ field }) => (
          <Checkbox
            label="I agree to receive marketing emails from Costa Coffee."
            checked={field.value}
            onChange={field.onChange}
          />
        )}
      />
      <div className="mx-auto !mt-10 text-center">
        <button disabled={isLoading} type="submit" className="btn">
          {isLoading && (
            <div className="px-[29.295px]">
              {isLoading && <Spinner size={16} color="text-white" />}
            </div>
          )}
          <span className={`${isLoading ? "hidden" : ""}`}>Enter now</span>
        </button>
      </div>
    </form>
  );
}
